import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  Animated,
} from 'react-native';
import { useAppTheme } from '../contexts/ThemeContext';

export default function Input({
  label,
  value,
  onChangeText,
  placeholder,
  error,
  multiline = false,
  keyboardType = 'default',
  secureTextEntry = false,
  icon = null,
  rightIcon = null,
  onRightIconPress,
  style,
  inputStyle,
  editable = true,
  autoCapitalize = 'sentences',
  returnKeyType,
  onSubmitEditing,
  maxLength,
  numberOfLines,
  onBlur,
}) {
  const { colors, spacing, radius, typography } = useAppTheme();
  const styles = useMemo(() => createStyles(colors, spacing, typography, radius), [colors]);

  const [focused, setFocused] = useState(false);
  const glowAnim = useRef(new Animated.Value(0)).current;
  const borderAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.timing(glowAnim, {
        toValue: focused ? 1 : 0,
        duration: 200,
        useNativeDriver: false,
      }),
      Animated.timing(borderAnim, {
        toValue: focused ? 1 : 0,
        duration: 180,
        useNativeDriver: false,
      }),
    ]).start();
  }, [focused]);

  const borderColor = borderAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [error ? colors.danger : colors.border, error ? colors.danger : colors.borderFocus],
  });

  const shadowOpacity = glowAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 0.25],
  });

  return (
    <View style={[styles.wrapper, style]}>
      {label ? (
        <Text style={[styles.label, focused && styles.labelFocused, error && styles.labelError]}>
          {label}
        </Text>
      ) : null}
      <Animated.View
        style={[
          styles.inputContainer,
          { borderColor },
          { shadowColor: error ? colors.danger : colors.accent, shadowOpacity, shadowOffset: { width: 0, height: 0 }, shadowRadius: 10, elevation: 0 },
          !editable && styles.disabled,
        ]}
      >
        {icon ? <View style={styles.iconLeft}>{icon}</View> : null}
        <TextInput
          style={[
            styles.input,
            multiline && styles.multiline,
            icon && styles.inputWithIcon,
            rightIcon && styles.inputWithRightIcon,
            inputStyle,
          ]}
          value={value}
          onChangeText={onChangeText}
          placeholder={placeholder}
          placeholderTextColor={colors.textMuted}
          secureTextEntry={secureTextEntry}
          keyboardType={keyboardType}
          multiline={multiline}
          numberOfLines={numberOfLines}
          onFocus={() => setFocused(true)}
          onBlur={() => { setFocused(false); onBlur?.(); }}
          editable={editable}
          autoCapitalize={autoCapitalize}
          returnKeyType={returnKeyType}
          onSubmitEditing={onSubmitEditing}
          maxLength={maxLength}
          textAlignVertical={multiline ? 'top' : 'center'}
        />
        {rightIcon ? (
          <TouchableOpacity
            style={styles.iconRight}
            onPress={onRightIconPress}
            activeOpacity={0.7}
          >
            {rightIcon}
          </TouchableOpacity>
        ) : null}
      </Animated.View>
      {error ? <Text style={styles.errorText}>{error}</Text> : null}
    </View>
  );
}

function createStyles(colors, spacing, typography, radius) {
  return StyleSheet.create({
    wrapper: {
      marginBottom: spacing.base,
    },
    label: {
      fontSize: typography.sizes.xs,
      fontWeight: typography.weights.semibold,
      color: colors.textMuted,
      marginBottom: spacing.xs + 2,
      letterSpacing: 0.8,
      textTransform: 'uppercase',
    },
    labelFocused: {
      color: colors.accent,
    },
    labelError: {
      color: colors.danger,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: colors.surfaceElevated,
      borderRadius: radius.md,
      borderWidth: 1.5,
      borderColor: colors.border,
      minHeight: 52,
    },
    disabled: {
      opacity: 0.45,
    },
    input: {
      flex: 1,
      paddingHorizontal: spacing.base,
      paddingVertical: spacing.md,
      fontSize: typography.sizes.base,
      color: colors.text,
      fontWeight: typography.weights.medium,
    },
    multiline: {
      minHeight: 100,
      paddingTop: spacing.md,
    },
    inputWithIcon: {
      paddingLeft: spacing.xs,
    },
    inputWithRightIcon: {
      paddingRight: spacing.xs,
    },
    iconLeft: {
      paddingLeft: spacing.base,
      opacity: 0.5,
    },
    iconRight: {
      paddingRight: spacing.base,
      opacity: 0.6,
    },
    errorText: {
      fontSize: typography.sizes.xs,
      color: colors.danger,
      marginTop: spacing.xs,
      marginLeft: 2,
      letterSpacing: 0.2,
    },
  });
}
